import { ColorUtils } from "@/lib/utils/color-utils";
import type { ServicePosition, ServiceWithTiming } from "../../utils/service-timing-utils";
import { getServiceDisplayMode, getWorkerDisplayText } from "../../utils/service-timing-utils";
import { ServiceWorkerSelector } from "./service-worker-selector";

interface ServiceSegmentProps {
  service: ServiceWithTiming;
  position: ServicePosition;
  appointmentId: string;
  isFirst?: boolean;
  isLast?: boolean;
  onClick?: () => void;
  className?: string;
  allowWorkerChange?: boolean;
}

export function ServiceSegment({
  service,
  position,
  appointmentId,
  isFirst = false,
  isLast = false,
  onClick,
  className = "",
  allowWorkerChange = true,
}: ServiceSegmentProps) {
  const displayMode = getServiceDisplayMode(position.height);
  const workerColor = ColorUtils.getColorStyle(service.worker?.color);

  // Determine display content based on height
  const getDisplayContent = () => {
    switch (displayMode) {
      case "minimal":
        return {
          showTime: false,
          showServiceName: false,
          showWorkerName: false,
          showDuration: false,
          workerText: service.worker ? getWorkerDisplayText(service.worker, true) : "?",
        };

      case "compact":
        return {
          showTime: true,
          showServiceName: true,
          showWorkerName: false,
          showDuration: false,
          workerText: getWorkerDisplayText(service.worker, true),
        };

      case "normal":
        return {
          showTime: true,
          showServiceName: true,
          showWorkerName: false,
          showDuration: false,
          workerText: getWorkerDisplayText(service.worker, true),
        };

      case "expanded":
        return {
          showTime: true,
          showServiceName: true,
          showWorkerName: true,
          showDuration: false,
          workerText: getWorkerDisplayText(service.worker, false),
        };
    }
  };

  const content = getDisplayContent();

  const Tag = onClick ? "button" : "div";
  return (
    <Tag
      {...(onClick ? { type: "button" as const } : {})}
      className={`
        relative flex flex-col w-full overflow-hidden
        ${!isFirst ? "border-t border-border/30" : ""}
        ${onClick ? "cursor-pointer hover:bg-accent/20" : ""}
        ${className}
      `}
      style={{ height: `${position.height}px` }}
      onClick={onClick}
      title={`${service.serviceName} - ${content.workerText} (${position.durationDisplay})`}
    >
      {/* Main content area */}
      <div className="flex-1 flex items-center justify-between px-2 py-1 min-h-0">
        {/* Service info */}
        <div className="flex-1 min-w-0 pr-1">
          {/* Time on separate line */}
          {content.showTime && (isFirst || displayMode === "expanded") && (
            <div className="text-md text-muted-foreground leading-none flex-shrink-0">
              {position.startTimeDisplay}
            </div>
          )}
          {/* Service name on separate line */}
          {content.showServiceName && (
            <div
              className={`
              font-medium truncate leading-tight flex-1 min-w-0
              ${
                displayMode === "minimal"
                  ? "text-[8px]"
                  : displayMode === "compact"
                    ? "text-[9px]"
                    : "text-[10px]"
              }
            `}
            >
              {service.serviceName}
            </div>
          )}

          {content.showWorkerName && service.worker && (
            <div className="text-xs text-muted-foreground truncate leading-tight">
              {content.workerText}
            </div>
          )}
        </div>

        {/* Worker indicator with selection */}
        <div className="flex-shrink-0 flex items-center">
          {allowWorkerChange ? (
            <ServiceWorkerSelector
              service={service}
              appointmentId={appointmentId}
              compact={displayMode === "minimal" || displayMode === "compact"}
            />
          ) : (
            service.worker && (
              <div
                className={`
                  rounded-full border-2 border-background shadow-sm flex items-center justify-center
                  ${
                    displayMode === "minimal"
                      ? "w-3 h-3"
                      : displayMode === "compact"
                        ? "w-4 h-4"
                        : "w-5 h-5"
                  }
                  ${workerColor.className}
                `}
                style={workerColor.style}
              >
                {displayMode === "minimal" && (
                  <span className="text-[6px] font-bold text-white mix-blend-difference">
                    {content.workerText.charAt(0)}
                  </span>
                )}
                {(displayMode === "compact" || displayMode === "normal") && (
                  <span className="text-[7px] font-bold text-white mix-blend-difference">
                    {content.workerText}
                  </span>
                )}
              </div>
            )
          )}

          {/* Remove unassigned text - don't show anything when no worker */}
        </div>
      </div>

      {/* Bottom border for visual separation (except last) */}
      {!isLast && displayMode !== "minimal" && (
        <div className="absolute bottom-0 left-2 right-2 h-px bg-border/20" />
      )}
    </Tag>
  );
}

/**
 * Simplified service segment for very small heights or combined display
 */
interface CompactServiceDisplayProps {
  services: ServiceWithTiming[];
  totalHeight: number;
  onClick?: () => void;
  className?: string;
}

export function CompactServiceDisplay({
  services,
  totalHeight,
  onClick,
  className = "",
}: CompactServiceDisplayProps) {
  const firstService = services[0];
  const serviceCount = services.length;

  // Get unique workers
  const uniqueWorkers = services.reduce(
    (acc, service) => {
      if (service.worker && !acc.find((w) => w.id === service.worker?.id)) {
        acc.push(service.worker);
      }
      return acc;
    },
    [] as NonNullable<ServiceWithTiming["worker"]>[]
  );

  // Check if there are any unassigned services
  const _hasUnassignedServices = services.some((service) => !service.worker);

  const displayText =
    serviceCount > 1 ? `${serviceCount} services` : firstService?.serviceName || "Service";

  const CompactTag = onClick ? "button" : "div";
  return (
    <CompactTag
      {...(onClick ? { type: "button" as const } : {})}
      className={`
        relative flex items-center justify-between w-full px-2 overflow-hidden
        ${onClick ? "cursor-pointer hover:bg-accent/20" : ""}
        ${className}
      `}
      style={{ height: `${totalHeight}px` }}
      onClick={onClick}
      title={services.map((s) => `${s.serviceName} - ${getWorkerDisplayText(s.worker)}`).join(", ")}
    >
      {/* Service info */}
      <div className="flex-1 min-w-0 pr-2">
        <div className="text-[10px] font-medium truncate leading-tight">{displayText}</div>
      </div>

      {/* Worker indicators */}
      <div className="flex-shrink-0 flex items-center space-x-1">
        {uniqueWorkers.slice(0, 3).map((worker, index) => {
          const workerColor = ColorUtils.getColorStyle(worker.color);
          return (
            <div
              key={worker.id}
              className={`
                w-4 h-4 rounded-full border-2 border-background shadow-sm
                ${index > 0 ? "-ml-2" : ""}
                ${workerColor.className}
              `}
              style={{
                ...workerColor.style,
                zIndex: uniqueWorkers.length - index,
              }}
              title={getWorkerDisplayText(worker)}
            />
          );
        })}
        {uniqueWorkers.length > 3 && (
          <div className="w-4 h-4 rounded-full bg-muted border-2 border-background shadow-sm -ml-2 flex items-center justify-center">
            <span className="text-[6px] font-bold text-muted-foreground">
              +{uniqueWorkers.length - 3}
            </span>
          </div>
        )}
        {/* Remove unassigned indicator */}
      </div>
    </CompactTag>
  );
}
